import { Component, inject, OnDestroy, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { DropdownMenusModule } from 'src/app/_metronic/partials';
import { DepartmentService } from 'src/app/services/api/departments/department.service';
import { DepartmentModel } from 'src/app/services/models/department.model';
import {
  createDatatableSortParams,
  DatatableSortChangeEvent,
  DatatableSortFieldMap,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { SharedModule } from 'src/shared/shared.module';

interface DepartmentRow {
  id: number;
  name: string;
}

interface DepartmentDataSource {
  data: DepartmentRow[];
  limit: number;
  offset: number;
  page: number;
  total: number;
  total_page: number;
}

@Component({
  selector: 'app-departments',
  imports: [
    SharedModule,
    TranslateModule,
    DropdownMenusModule,
    ReactiveFormsModule,
  ],
  templateUrl: './departments.component.html',
  styleUrl: './departments.component.scss',
})
export class DepartmentsComponent implements OnInit, OnDestroy {
  @ViewChild('rowIndexTemplate', { static: true })
  rowIndexTemplate!: TemplateRef<any>;

  form!: FormGroup;
  columns: any[] = [];
  departments = signal<DepartmentDataSource>(this.createDepartmentDataSource([]));
  tableLoading = signal(false);
  pageSizeOptions = [10, 20, 50];

  private fb = inject(FormBuilder);
  private departmentService = inject(DepartmentService);
  private currentPage = 1;
  private currentLimit = 10;
  private currentSortParams: DatatableSortParams = {
    sort: null,
    direction: null,
  };
  private keywordSearchSubscription?: Subscription;
  private readonly sortFieldMap: DatatableSortFieldMap = {
    index: 'sequence',
    name: 'name',
  };

  ngOnInit(): void {
    this.initForm();
    this.initColumns();
    this.listenKeywordSearch();
    this.loadDepartments();
  }

  ngOnDestroy(): void {
    this.keywordSearchSubscription?.unsubscribe();
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadDepartments();
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.departments().total_page || page === this.currentPage) {
      return;
    }

    this.currentPage = page;
    this.loadDepartments();
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.currentPage = 1;
    this.loadDepartments();
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.currentPage = 1;
    this.loadDepartments();
  }

  private initForm(): void {
    this.form = this.fb.group({
      keyword: [''],
    });
  }

  private listenKeywordSearch(): void {
    this.keywordSearchSubscription = this.form.get('keyword')?.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        this.currentPage = 1;
        this.loadDepartments();
      });
  }

  private initColumns(): void {
    this.columns = [
      {
        prop: 'index',
        name: 'ลำดับ',
        sortable: true,
        cellTemplate: this.rowIndexTemplate,
        width: 64,
        minWidth: 64,
        maxWidth: 64,
      },
      {
        prop: 'name',
        name: 'สาขาวิชา',
        sortable: true,
      },
    ];
  }

  private loadDepartments(): void {
    this.tableLoading.set(true);

    this.departmentService.getDepartments({
      name: String(this.form.getRawValue().keyword ?? '').trim(),
      page: this.currentPage,
      limit: this.currentLimit,
      sort: this.currentSortParams.sort,
      direction: this.currentSortParams.direction,
    }).subscribe({
      next: (response) => {
        this.departments.set(this.createDepartmentDataSource(
          response.data,
          response.page,
          response.limit,
          response.total,
          response.total_pages,
        ));
        this.tableLoading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.departments.set(this.createDepartmentDataSource([], 1, this.currentLimit, 0, 1));
        this.tableLoading.set(false);
      },
    });
  }

  private createDepartmentDataSource(
    items: DepartmentModel[],
    page = this.currentPage,
    limit = this.currentLimit,
    total = items.length,
    totalPage = Math.max(1, Math.ceil(total / limit)),
  ): DepartmentDataSource {
    const normalizedTotalPage = Math.max(1, totalPage);
    const normalizedPage = Math.min(page, normalizedTotalPage);
    const offset = (normalizedPage - 1) * limit;
    const data = items
      .map((item) => ({
        id: item.id,
        sequence: item.sequence,
        name: item.name,
      }));

    this.currentPage = normalizedPage;
    this.currentLimit = limit;

    return {
      data,
      limit,
      offset,
      page: normalizedPage,
      total,
      total_page: normalizedTotalPage,
    };
  }
}
